// src/components/MobileButton.jsx
import React from 'react';
import { Link } from 'react-router-dom';

/**
 * A mobile-optimized button component with proper touch targets
 * 
 * @param {Object} props Component props
 * @param {React.ReactNode} props.children Button content
 * @param {string} props.variant Button style variant ('primary', 'secondary', 'outline', 'ghost', 'danger')
 * @param {string} props.size Button size ('small', 'medium', 'large')
 * @param {string} props.className Additional CSS classes
 * @param {string} props.href Optional href - converts button to an anchor tag
 * @param {string} props.to Optional to prop - converts button to a React Router Link
 * @param {boolean} props.isLoading Optional loading state
 * @param {boolean} props.disabled Whether the button is disabled
 * @param {boolean} props.fullWidth Whether the button should take full width
 * @param {Function} props.onClick Click handler
 * @param {React.ReactNode} props.leftIcon Optional icon to show at the left
 * @param {React.ReactNode} props.rightIcon Optional icon to show at the right
 */
const MobileButton = ({
  children,
  variant = 'primary',
  size = 'medium',
  className = '',
  href,
  to,
  isLoading = false,
  disabled = false,
  fullWidth = false,
  onClick,
  leftIcon,
  rightIcon,
  ...rest
}) => {
  // Variant styles
  const variantStyles = {
    primary: 'bg-slate-600 hover:bg-slate-700 text-white border-transparent',
    secondary: 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-transparent',
    outline: 'bg-transparent hover:bg-slate-50 text-slate-700 border-slate-300 hover:border-slate-400',
    ghost: 'bg-transparent hover:bg-slate-50 text-slate-700 border-transparent',
    danger: 'bg-red-600 hover:bg-red-700 text-white border-transparent'
  };

  // Size styles
  const sizeStyles = {
    small: 'text-xs py-1 px-2',
    medium: 'text-sm py-2 px-3',
    large: 'text-base py-2 px-4'
  };

  // Disabled styles
  const disabledStyles = disabled || isLoading
    ? 'opacity-60 cursor-not-allowed'
    : 'cursor-pointer';

  // Full width styles
  const widthStyles = fullWidth 
    ? 'w-full flex justify-center'
    : 'inline-flex';

  // Apply all styles
  const buttonClasses = `
    ${widthStyles}
    items-center
    rounded
    font-medium
    border
    shadow-sm
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500
    transition-colors
    mobile-touch-target
    ${variantStyles[variant] || variantStyles.primary}
    ${sizeStyles[size] || sizeStyles.medium}
    ${disabledStyles}
    ${className}
  `;

  // Rendering logic for different button types
  const ButtonContent = () => (
    <>
      {isLoading && (
        <span className="mr-2">
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </span>
      )}
      {leftIcon && <span className="mr-2">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="ml-2">{rightIcon}</span>}
    </>
  );

  // If it's a link
  if (href) {
    return (
      <a 
        href={href} 
        className={buttonClasses}
        {...rest}
      >
        <ButtonContent />
      </a>
    );
  }

  // If it's a router link
  if (to) {
    return (
      <Link 
        to={to} 
        className={buttonClasses}
        {...rest}
      >
        <ButtonContent />
      </Link>
    );
  }

  // Default button
  return (
    <button 
      className={buttonClasses}
      disabled={disabled || isLoading}
      onClick={onClick}
      type={rest.type || 'button'}
      {...rest}
    >
      <ButtonContent />
    </button>
  );
};

export default MobileButton;